import { useState, useEffect, useRef, ReactNode } from "react";

const BRAND = {
    purple: "#7B5EA7",
    purpleLight: "#9B7EC8",
    purpleDark: "#3D2A6B",
    purpleDeep: "#1A0F3D",
    accent: "#C8B8E8",
};

interface Stream {
    id: string;
    label: string;
    shortLabel: string;
    value: number;
    pct: string;
    stat: string;
    statLabel: string;
    color: string;
    desc: string;
    icon: ReactNode;
}

const streams: Stream[] = [
    {
        id: "lease",
        label: "Lease Margins",
        shortLabel: "Lease",
        value: 42,
        pct: "42%",
        stat: "₦46M",
        statLabel: "annual yield",
        color: "#9B7EC8",
        desc: "Fixed returns on owned hardware across all hubs",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
        ),
    },
    {
        id: "rev",
        label: "Revenue Sharing",
        shortLabel: "Rev Share",
        value: 28,
        pct: "28%",
        stat: "18.4%",
        statLabel: "avg asset utilization",
        color: "#C8B8E8",
        desc: "% of production output from each financed asset",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                <polyline points="16 7 22 7 22 13"/>
            </svg>
        ),
    },
    {
        id: "bricks",
        label: "Bricks & Materials",
        shortLabel: "Bricks",
        value: 19,
        pct: "19%",
        stat: "₦12M",
        statLabel: "estimated materials revenue",
        color: "#7B5EA7",
        desc: "Margins on Eko Bricks sold to construction firms",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <rect x="2" y="14" width="8" height="4" rx="0.5"/>
                <rect x="14" y="14" width="8" height="4" rx="0.5"/>
                <rect x="8" y="9" width="8" height="4" rx="0.5"/>
                <rect x="2" y="4" width="8" height="4" rx="0.5"/>
                <rect x="14" y="4" width="8" height="4" rx="0.5"/>
            </svg>
        ),
    },
    {
        id: "data",
        label: "Data Services",
        shortLabel: "Data",
        value: 11,
        pct: "11%",
        stat: "1 buyer",
        statLabel: "institutional clients",
        color: "#5A3E8A",
        desc: "Supply chain analytics for institutional buyers",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
            </svg>
        ),
    },
];

function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
    const start = polarToXY(cx, cy, r, startAngle);
    const end = polarToXY(cx, cy, r, endAngle);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
}

interface DonutChartProps {
    active: string | null;
    onHover: (id: string | null) => void;
}

function DonutChart({ active, onHover }: DonutChartProps) {
    const cx = 130, cy = 130, r = 100, inner = 65;
    // Larger gap prevents rounded caps from adjacent segments bleeding into each other
    const gap = 4;
    let cursor = 0;

    const slices = streams.map((s) => {
        const sweep = (s.value / 100) * 360;
        const start = cursor + gap / 2;
        const end = cursor + sweep - gap / 2;
        cursor += sweep;
        return { ...s, start, end };
    });

    return (
        // Extra viewBox padding (20px each side) so thick strokes never clip
        <svg viewBox="-20 -20 300 300" width="270" height="270">
            <defs>
                {slices.map((s) => (
                    <filter key={s.id} id={`glow-${s.id}`} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="4" result="blur" />
                        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                ))}
            </defs>

            <circle cx={cx} cy={cy} r={r + 8} fill="rgba(255,255,255,0.03)" />

            {slices.map((s) => {
                const isActive = active === s.id;
                // strokeWidth grows inward/outward symmetrically — no radius change needed
                return (
                    <path
                        key={s.id}
                        d={describeArc(cx, cy, r, s.start, s.end)}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={isActive ? 26 : 18}
                        // butt caps = no extension beyond arc endpoints — eliminates out-of-bounds
                        strokeLinecap="butt"
                        filter={isActive ? `url(#glow-${s.id})` : undefined}
                        style={{
                            cursor: "pointer",
                            transition: "stroke-width 0.25s ease, opacity 0.25s ease",
                            opacity: active && !isActive ? 0.3 : 1,
                        }}
                        onMouseEnter={() => onHover(s.id)}
                        onMouseLeave={() => onHover(null)}
                    />
                );
            })}

            {/* Inner hole always on top to mask any stroke overflow inward */}
            <circle cx={cx} cy={cy} r={inner} fill="#1A0F3D" />

            {active ? (
                <>
                    <text x={cx} y={cy - 10} textAnchor="middle" fill="#C8B8E8" fontSize="12" fontFamily="DM Sans, sans-serif" fontWeight="600" letterSpacing="0.08em">
                        {streams.find(s => s.id === active)?.pct}
                    </text>
                    <text x={cx} y={cy + 8} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="DM Sans, sans-serif">
                        {streams.find(s => s.id === active)?.shortLabel}
                    </text>
                </>
            ) : (
                <>
                    <text x={cx} y={cy - 8} textAnchor="middle" fill="white" fontSize="14" fontFamily="Instrument Serif, serif" fontWeight="400">
                        Revenue
                    </text>
                    <text x={cx} y={cy + 10} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8.5" fontFamily="DM Sans, sans-serif" letterSpacing="0.12em">
                        MIX
                    </text>
                </>
            )}
        </svg>
    );
}

export default function RevenueGraphic() {
    const [active, setActive] = useState<string | null>(null);
    const [barWidths, setBarWidths] = useState<number[]>(streams.map(() => 0));
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const io = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setTimeout(() => setBarWidths(streams.map(s => s.value)), 200);
                io.disconnect();
            }
        }, { threshold: 0.3 });
        if (ref.current) io.observe(ref.current);
        return () => io.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{
                background: "linear-gradient(145deg, #1A0F3D 0%, #3D2A6B 60%, #7B5EA7 100%)",
                borderRadius: "24px",
                padding: "36px",
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "white",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 32px 80px rgba(26,15,61,0.5)",
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            <div style={{ position: "absolute", top: -80, right: -80, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(155,126,200,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(200,184,232,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />

            <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: BRAND.accent, fontWeight: 700, marginBottom: 6 }}>
                    Revenue Engine
                </div>
                <div style={{ fontSize: "1.4rem", fontFamily: "'Instrument Serif', serif", lineHeight: 1.2, color: "white" }}>
                    Four Compounding Streams
                </div>
            </div>

            <div style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flexShrink: 0 }}>
                    <DonutChart active={active} onHover={setActive} />
                </div>

                <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 14 }}>
                    {streams.map((s, i) => {
                        const isActive = active === s.id;
                        return (
                            <div
                                key={s.id}
                                onMouseEnter={() => setActive(s.id)}
                                onMouseLeave={() => setActive(null)}
                                style={{ cursor: "pointer", opacity: active && !isActive ? 0.4 : 1, transition: "opacity 0.3s ease" }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                        <div style={{ color: s.color, opacity: 0.9 }}>{s.icon}</div>
                                        <span style={{ fontSize: "0.82rem", fontWeight: 600, color: isActive ? "white" : "rgba(255,255,255,0.75)", transition: "color 0.2s" }}>
                                            {s.label}
                                        </span>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <span style={{ fontSize: "0.95rem", fontWeight: 700, color: s.color }}>{s.stat}</span>
                                        <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)", marginLeft: 4 }}>{s.statLabel}</span>
                                    </div>
                                </div>

                                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 6, height: 7, overflow: "hidden" }}>
                                    <div style={{
                                        height: "100%",
                                        width: `${barWidths[i]}%`,
                                        background: `linear-gradient(90deg, ${s.color}99, ${s.color})`,
                                        borderRadius: 6,
                                        transition: "width 1s cubic-bezier(0.16,1,0.3,1)",
                                        transitionDelay: `${i * 0.12}s`,
                                        boxShadow: isActive ? `0 0 12px ${s.color}88` : "none",
                                    }} />
                                </div>

                                <div style={{
                                    fontSize: "0.72rem",
                                    color: "rgba(200,184,232,0.65)",
                                    marginTop: 4,
                                    maxHeight: isActive ? 24 : 0,
                                    overflow: "hidden",
                                    transition: "max-height 0.3s ease",
                                    lineHeight: 1.5,
                                }}>
                                    {s.desc}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                    <div style={{ fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 3 }}>Pilot Repayment Rate</div>
                    <div style={{ fontSize: "2rem", fontFamily: "'Instrument Serif', serif", color: "white", lineHeight: 1 }}>100%</div>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 3 }}>Assets Financed (Pilot)</div>
                    <div style={{ fontSize: "2rem", fontFamily: "'Instrument Serif', serif", color: BRAND.accent, lineHeight: 1 }}>₦100M+</div>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 3 }}>Active Partners</div>
                    <div style={{ fontSize: "2rem", fontFamily: "'Instrument Serif', serif", color: "white", lineHeight: 1 }}>3</div>
                </div>
            </div>
        </div>
    );
}